import React, { useRef } from 'react';
import { Box, Divider } from '@mui/material';
import {
  FormProps,
  FormHeader,
  ClientNameField,
  DocumentUploadArea,
  FactsDescriptionField,
  PieceToRebutField,
  AudioRecordingSection,
  WritingStyleSelector,
  InfoTip,
} from './shared';

// Peças que podem ser rebatidas em um recurso ordinário trabalhista
const PECAS_REBATER_RO = [
  'Sentença',
  'Sentença de Embargos à Execução',
  'Sentença de Embargos de Terceiro',
  'Outro'
];

const RecursoOrdinarioTrabalhistaForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedStyle, setSelectedStyle] = React.useState(values.estilo_escrita || 'padrao');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Recurso Ordinário Trabalhista"
        customDescription="Recurso contra sentença proferida em primeira instância trabalhista"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 1. Nome do Cliente */}
        <ClientNameField
          value={values.nome_cliente || ''}
          onChange={(value) => handleFieldChange('nome_cliente', value)}
          label="Escreva o nome do seu cliente:"
        />

        {/* 2. Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Processo na íntegra e Sentença trabalhista."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* 3. Descrição dos Fatos (Opcional) */}
        <FactsDescriptionField
          value={values.fatos_caso || ''}
          onChange={(value) => handleFieldChange('fatos_caso', value)}
          placeholder="Descreva os pontos da sentença que deseja reformar, os pedidos não acolhidos, as provas desconsideradas, etc..."
        />

        {/* 4. Peça que deseja rebater */}
        <PieceToRebutField
          value={values.peca_rebater || 'Sentença'}
          onChange={(value) => handleFieldChange('peca_rebater', value)}
          otherValue={values.peca_rebater_outro || ''}
          onOtherChange={(value) => handleFieldChange('peca_rebater_outro', value)}
          options={PECAS_REBATER_RO}
        />

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio descrevendo os pontos a serem reformados que a IA irá transcrever automaticamente."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Estilo de Escrita */}
      <WritingStyleSelector
        selectedStyle={selectedStyle}
        onStyleChange={(styleId) => {
          setSelectedStyle(styleId);
          handleFieldChange('estilo_escrita', styleId);
        }}
      />

      <InfoTip message="O recurso ordinário é o principal recurso contra sentenças trabalhistas. Anexe a sentença e o processo na íntegra para que a IA possa identificar os pontos a serem reformados e fundamentar adequadamente o recurso." />
    </Box>
  );
};

export default RecursoOrdinarioTrabalhistaForm;

